import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router, RouterModule } from '@angular/router';
import { LocalStorageService } from '../../core/services/local-storage/local-storage.service';
import { LoginModel } from '../../core/models/login/login.model';
import { UserAuthenticationService } from '../../core/services/user-authentication/user-authentication.service';
import { HttpStatusCode } from 'axios';
import { SessionStorageService } from '../../core/services/session-storage/session-storage.service';
import { CurrentUserInfo } from '../../core/interfaces/current-user-info.interface';
import { LoginInfo } from '../../core/interfaces/login-info.interface';
import { SnackbarService } from '../../shared/services/snackbar/snackbar.service';
import { MaintenanceService } from '../../core/services/maintenance/maintenance.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    RouterModule
  ],
  templateUrl: './login.page.html',
  styleUrl: './login.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginPage implements OnInit {

  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private localStorageService = inject(LocalStorageService);
  private sessionStorageService = inject(SessionStorageService);
  private snackBarService = inject(SnackbarService);
  private userAuthenticationService = inject(UserAuthenticationService);
  private maintenanceService = inject(MaintenanceService);

  loginFg!: FormGroup;

  passwordHide = signal(true);
  private saveInfoFlag: boolean = false;

  private loginInfo!: LoginInfo;
  private login = new LoginModel();

  protected isLoginLoading = signal(false);
  protected isMaintenanceMode = signal(false);
  protected maintenanceType = signal("");
  protected maintenanceMessage = signal("");

  protected isOtpVerificationEnabled: boolean = false;

  constructor() { }

  async ngOnInit(): Promise<void> {

    this.isOtpVerificationEnabled = environment.security.enableOtpVerification;

    this.loginInfo = this.localStorageService.getLoginInfo();

    this.login.setUsername(this.loginInfo.username || '');
    this.login.setPassword(this.loginInfo.password || '');

    if (this.login.getUsername()) {
      this.saveInfoFlag = true;
    }

    this.loginFg = this.formBuilder.group({
      username: [this.login.getUsername(), [Validators.required, Validators.email]],
      password: [this.login.getPassword(), [Validators.required]],
      saveInfoCb: [this.saveInfoFlag]
    });

    // Checking maintenance configurations  
    const responseData = await this.maintenanceService.getMaintenanceConfig()
      .then((response) => {
        return response;
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });

    if (responseData.status === HttpStatusCode.Ok) {
      this.isMaintenanceMode.set(responseData.data.isMaintenanceMode);
      this.maintenanceType.set(responseData.data.maintenanceType.toString());
      this.maintenanceMessage.set(responseData.data.maintenanceMessage.toString());
    }
  }

  passwordHideToggle(event: MouseEvent) {
    this.passwordHide.set(!this.passwordHide());
    event.stopPropagation();
  }

  async authenticateUser() {
    const saveInfoCbFlag: boolean = this.loginFg.get('saveInfoCb')?.value;
    this.login.setUsername(
      this.loginFg.get("username")?.value?.toString() || ''
    );
    this.login.setPassword(
      this.loginFg.get("password")?.value?.toString() || ''
    );

    // Storing the login credentials if "Remember me" checkbox is checked.
    if (saveInfoCbFlag) {
      this.loginInfo.username = this.login.getUsername();
      this.loginInfo.password = this.login.getPassword();

      this.localStorageService.setLoginInfo(this.loginInfo);
    } else {
      this.localStorageService.clearLocalStorage();
    }

    // Triggering Login API call.
    this.isLoginLoading.set(true);

    if (this.isOtpVerificationEnabled) {
      const responseData = await this.userAuthenticationService
        .authenticateCredentialsMfa(this.login)
        .then((response) => {
          return JSON.parse(response);
        })
        .catch((error) => {
          // console.error(error);
          return error;
        });

      // Handling the response from the API call.
      if (responseData.status === HttpStatusCode.Ok) {
        const currentUserInfo: CurrentUserInfo = {
          email: this.login.getUsername(),
          accessToken: responseData.data.accesstoken,
          password: this.login.getPassword()
        };

        this.sessionStorageService.setCurrentUserInfo(currentUserInfo);

        // Routing to Home.
        this.router.navigate(['/otp-verification'], {
          state: {
            returnPath: "home"
          }
        });
      } else {
        if (responseData.code === HttpStatusCode.BadRequest) {
          // Display notification on login failure.
          this.snackBarService.openFailureSnackBar("Invalid login credentials.");
        }

        this.sessionStorageService.clearSessionStorage();
      }

    } else {
      const responseData = await this.userAuthenticationService
        .authenticateCredentials(this.login)
        .then((response) => {
          return JSON.parse(response);
        })
        .catch((error) => {
          // console.error(error);
          return error;
        });

      // Handling the response from the API call.
      if (responseData.status === HttpStatusCode.Ok) {
        const currentUserInfo: CurrentUserInfo = {
          repCode: responseData.data.responsedata.piasRepCode,
          email: responseData.data.responsedata.email,
          authToken: responseData.data.token,
          personName: responseData.data.responsedata.personName,
          userRole: responseData.data.responsedata.userRole,
          accessToken: responseData.data.accesstoken,
          userGroup: responseData.data.responsedata.usergroup
        };

        this.sessionStorageService.setCurrentUserInfo(currentUserInfo);
        
        // Routing to Dashboard.
        this.router.navigate(['/dashboard']);
      } else {
        if (responseData.code === HttpStatusCode.BadRequest) {
          // Display notification on login failure.
          this.snackBarService.openFailureSnackBar("Invalid login credentials.");
        }

        this.sessionStorageService.clearSessionStorage();
      }

    }

    this.isLoginLoading.set(false);



  }

}
